/**
 * TextBlock - Text element for DesignerCanvas
 *
 * A simple text block that can be edited on double-click.
 */

import { useState, useCallback, useRef, useEffect, memo } from 'react';
import type { TextBlockElement } from '@deep-agents/shared-types';

interface TextBlockProps {
  element: TextBlockElement;
  onUpdate?: (props: Partial<TextBlockElement['props']>) => void;
}

// Map fontSize to Tailwind classes
const fontSizeClasses = {
  sm: 'text-sm',
  base: 'text-base',
  lg: 'text-lg',
  xl: 'text-xl',
  '2xl': 'text-2xl',
};

// Map fontWeight to Tailwind classes
const fontWeightClasses = {
  normal: 'font-normal',
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold',
};

// Map textAlign to Tailwind classes
const textAlignClasses = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
};

export const TextBlock = memo(({ element, onUpdate }: TextBlockProps) => {
  const { content, fontSize = 'base', fontWeight = 'normal', textAlign = 'left' } = element.props;

  const [isEditing, setIsEditing] = useState(false);
  const [localContent, setLocalContent] = useState(content);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Sync local content with prop changes
  useEffect(() => {
    setLocalContent(content);
  }, [content]);

  const handleDoubleClick = useCallback(() => {
    setIsEditing(true);
  }, []);

  const handleBlur = useCallback(() => {
    setIsEditing(false);
    if (localContent !== content) {
      onUpdate?.({ content: localContent });
    }
  }, [localContent, content, onUpdate]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setLocalContent(content); // Reset to original
      setIsEditing(false);
    } else if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleBlur();
    }
  }, [content, handleBlur]);

  const sizeClass = fontSizeClasses[fontSize];
  const weightClass = fontWeightClasses[fontWeight];
  const alignClass = textAlignClasses[textAlign];

  return (
    <div
      className={`w-full h-full p-2 ${sizeClass} ${weightClass} ${alignClass} text-slate-700`}
      onDoubleClick={handleDoubleClick}
    >
      {isEditing ? (
        <textarea
          ref={inputRef}
          value={localContent}
          onChange={(e) => setLocalContent(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          className="w-full h-full bg-transparent border-none outline-none resize-none"
          style={{ fontSize: 'inherit', fontWeight: 'inherit', textAlign: 'inherit' }}
        />
      ) : (
        <div className="w-full h-full overflow-hidden">
          {content || 'Double-click to edit'}
        </div>
      )}
    </div>
  );
});

TextBlock.displayName = 'TextBlock';
